/**
 * HeaderSticky.tsx
 *
 * 负责控制顶部粘性 Header 的纵向位移
 * 仅通过修改 DOM 节点 style 实现，不渲染任何 UI
 *
 * 重构目标：
 * 1. 提高可读性与可维护性，减少魔法数字
 * 2. 保持原有功能、依赖与对外接口不变
 * 3. 细化职责：滚动逻辑、样式更新、生命周期初始化拆分为更小函数
 */
'use client';
import { useCallback, useEffect, useRef, useState } from 'react';

/* -------------- 常量配置 -------------- */
const STICKY_HEADER_ID = 'sticky-header'; // DOM ID，保持不变，便于统一维护
const BENEFIT_BAR_ID = 'benefit-bar-wrap'; // 心智DOM ID，保持不变，便于统一维护

/**
 * 将像素值写入 DOM 节点 style.top
 */
function applyTopStyle(element: Element | null, top: number): void {
  if (element) {
    element.setAttribute('style', `top: ${top}px;`);
  }
}

/* ------------- 组件实现 -------------- */
const HeaderSticky = () => {
  /* ------------------ 计算边界值 ------------------ */
  const [minTop, setMinTop] = useState<number>(0);
  const [maxTop, setMaxTop] = useState<number>(0);

  /* ------------------ 状态管理（引用） ------------------ */
  // 当前 Header 的 style.top 值
  const currentTopRef = useRef<number>(minTop);
  // 上一次滚动位置，用于计算方向
  const lastScrollYRef = useRef<number>(0);
  // 是否已在 rAF 队列中，避免重复调度
  const tickingRef = useRef<boolean>(false);
  // 指向真实 DOM
  const stickyHeaderRef = useRef<Element | null>(null);

  /* ------------------ 核心逻辑 ------------------ */
  /**
   * 将 top 值限制在 [minTop, maxTop] 并同步到 DOM
   */
  const syncTopStyle = useCallback(
    (newTop: number) => {
      const clampedTop = Math.min(Math.max(newTop, minTop), maxTop);
      currentTopRef.current = clampedTop;
      applyTopStyle(stickyHeaderRef.current, clampedTop);
    },
    [minTop, maxTop],
  );

  /**
   * 滚动事件回调
   * 使用 requestAnimationFrame 降低重排频率
   */
  const handleScroll = useCallback(() => {
    if (tickingRef.current) return;

    tickingRef.current = true;

    window.requestAnimationFrame(() => {
      const currentScrollY = window.scrollY || 0;
      const deltaY = lastScrollYRef.current - currentScrollY;

      // 根据滚动方向调整 Header 位置
      syncTopStyle(currentTopRef.current + deltaY);

      lastScrollYRef.current = currentScrollY;
      tickingRef.current = false;
    });
  }, [syncTopStyle]);

  /* ------------------ 初始化与清理 ------------------ */
  useEffect(() => {
    // 首次挂载时缓存 DOM 引用
    const headerElement = document.getElementById(STICKY_HEADER_ID);
    if (headerElement) {
      setMinTop(-headerElement.offsetHeight);
      stickyHeaderRef.current = headerElement;
    }
    const benefitBarElement = document.getElementById(BENEFIT_BAR_ID);
    if (benefitBarElement) {
      setMaxTop(-benefitBarElement.offsetHeight);
    }
  }, []);

  useEffect(() => {
    // 记录当前滚动位置作为基准
    lastScrollYRef.current = window.scrollY || 0;

    // 注册滚动监听
    window.addEventListener('scroll', handleScroll, { passive: true });

    // 组件卸载时清理
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  /* ------------------ 渲染 ------------------ */
  // 该组件无 UI，仅负责控制外部 DOM
  return null;
};

export default HeaderSticky;
